import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Paper,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Pagination,
  CircularProgress,
  Chip,
  InputAdornment,
  Autocomplete,
  Collapse,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import FilterListIcon from "@mui/icons-material/FilterListOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import CallIcon from "@mui/icons-material/Call";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import api from "../../services/api";
import {
  fetchTickets,
  fetchHeadings,
  fetchResolvedByOptions,
  fetchAdminUsersForTickets,
  setQuery,
} from "./ticketsSlice";
import TicketCreateDialog from "./TicketCreateDialog";
import TicketDetailDrawer from "./TicketDetailDrawer";
import MasterListsDialog from "./MasterListsDialog";
import {
  readTicketFavorites,
  writeTicketFavorites,
  isTicketFavorite,
  toggleTicketFavorite,
} from "./favorites";

const telHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
};

// Mirrors the colour map used in the Companies list and Search page so the
// contact cell looks identical across modules.
const CONTACT_COLORS = {
  operator:    { idle: "#a78bfa", hover: "#8b5cf6" }, // violet
  electrician: { idle: "#fbbf24", hover: "#f59e0b" }, // amber
  secretary:   { idle: "#34d399", hover: "#10b981" }, // mint/teal
};

// 3-row contact block — same fixed-width grid as Companies/Search so
// icon/label/name/phone line up vertically across every row.
const TableContactCell = ({ company }) => {
  const c = company || {};
  const rows = [
    { label: "Operator",    name: c.operator_name,     phone: c.operator_phone,           colors: CONTACT_COLORS.operator },
    { label: "Electrician", name: null,                phone: c.local_electrician_number, colors: CONTACT_COLORS.electrician },
    { label: "Secretary",   name: c.secretary_name,    phone: c.secretary_phone,          colors: CONTACT_COLORS.secretary },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      {rows.map((r) => {
        const callable = Boolean(r.phone);
        return (
          <Box
            key={r.label}
            onClick={callable ? (e) => e.stopPropagation() : undefined}
            component={callable ? "a" : "div"}
            href={callable ? telHref(r.phone) : undefined}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              borderRadius: "4px",
              py: 0.25,
              minWidth: 0,
              textDecoration: "none",
              color: "inherit",
              ...(callable && {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  "& .row-call-btn": { backgroundColor: r.colors.hover },
                },
              }),
            }}
          >
            <Box
              className="row-call-btn"
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.125,
                backgroundColor: callable ? r.colors.idle : "rgba(0,0,0,0.08)",
                transition: "background-color 0.15s",
                "& .MuiSvgIcon-root": {
                  color: callable ? "#ffffff" : "rgba(0,0,0,0.3)",
                  fontSize: 11,
                },
              }}
            >
              <CallIcon />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                lineHeight: 1.35,
                // Wrap rather than truncate so long names + numbers stay
                // fully visible (matches the Companies contact cell).
                whiteSpace: "normal",
                wordBreak: "break-word",
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
                {r.label}
              </Box>
              <Box component="span" sx={{ color: r.name ? "text.primary" : "text.disabled", mx: 0.25 }}>
                -{r.name || "—"}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 500,
                  color: r.phone ? "text.primary" : "text.disabled",
                }}
              >
                -{r.phone || "—"}
              </Box>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// Click-to-toggle star — yellow when favorited, outline when not.
// Stops row-click propagation so tapping the star never opens the drawer.
const FavoriteStar = ({ active, onToggle, size = 18 }) => (
  <IconButton
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      onToggle?.();
    }}
    sx={{
      p: 0.5,
      color: active ? "#f59e0b" : "text.disabled",
      "&:hover": { backgroundColor: "rgba(245,158,11,0.12)" },
    }}
  >
    {active ? (
      <StarIcon sx={{ fontSize: size }} />
    ) : (
      <StarBorderIcon sx={{ fontSize: size }} />
    )}
  </IconButton>
);

const TypeChip = ({ type }) => {
  if (!type) return null;
  return (
    <Chip
      label={type === "motor" ? "MOTOR" : "OHT"}
      size="small"
      sx={{
        height: 16,
        fontSize: "0.6rem",
        fontWeight: 700,
        backgroundColor: type === "motor" ? "#0e7490" : "#0d9488",
        color: "#fff",
        "& .MuiChip-label": { px: 0.5 },
      }}
    />
  );
};

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const PRIORITY_COLOR = {
  low: { bg: "#e0f2fe", fg: "#0369a1" },
  medium: { bg: "#fef3c7", fg: "#b45309" },
  high: { bg: "#fde7d7", fg: "#c2410c" },
  critical: { bg: "#fee2e2", fg: "#b91c1c" },
};

const PRIORITY_LABEL = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

const PriorityChip = ({ priority }) => {
  const p = priority || "medium";
  const c = PRIORITY_COLOR[p] || PRIORITY_COLOR.medium;
  return (
    <Chip
      label={PRIORITY_LABEL[p] || p}
      size="small"
      sx={{
        height: 18,
        fontSize: "0.6875rem",
        borderRadius: "2px",
        backgroundColor: c.bg,
        color: c.fg,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
};

const STATUS_LABELS = {
  open: "Open",
  ongoing: "Ongoing",
  waiting_user_feedback: "Waiting · feedback",
  waiting_material: "Waiting · material",
  waiting_service: "Waiting · service",
  resolved: "Resolved",
  removed: "Removed",
};

const STATUS_COLOR = {
  open: { bg: "#fdecec", fg: "#bb0000" },
  ongoing: { bg: "#fdf2e6", fg: "#df6e0c" },
  waiting_user_feedback: { bg: "#f3eafd", fg: "#6f3cb1" },
  waiting_material: { bg: "#f3eafd", fg: "#6f3cb1" },
  waiting_service: { bg: "#f3eafd", fg: "#6f3cb1" },
  resolved: { bg: "#e8f5ec", fg: "#107e3e" },
  removed: { bg: "#f1f1f1", fg: "#6a6d70" },
};

// Pill rendered as an inline-block Box so multi-word statuses
// ("Waiting user feedback") can wrap to a second line instead of being
// truncated with `...` inside an MUI Chip.
const StatusChip = ({ status }) => {
  const c = STATUS_COLOR[status] || STATUS_COLOR.open;
  return (
    <Box
      sx={{
        display: "inline-block",
        backgroundColor: c.bg,
        color: c.fg,
        fontSize: "0.6875rem",
        fontWeight: 600,
        borderRadius: "2px",
        px: 0.75,
        py: 0.25,
        lineHeight: 1.2,
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: "100%",
      }}
    >
      {STATUS_LABELS[status] || status}
    </Box>
  );
};

const fieldSx = { "& .MuiInputBase-input": { fontSize: "0.8125rem" } };

// Easy-to-identify style for dropdowns / autocompletes so they stand out
// clearly from plain text inputs — teal border + teal chevron + teal label.
// No background fill (kept the input white per latest design pass).
const dropdownFieldSx = {
  ...fieldSx,
  "& .MuiInputLabel-root": {
    color: "primary.main",
    fontWeight: 600,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "rgba(13,148,136,0.45)",
      borderWidth: "1.5px",
    },
    "&:hover": {
      "& fieldset": { borderColor: "primary.main" },
    },
    "&.Mui-focused": {
      "& fieldset": { borderColor: "primary.main", borderWidth: "1.5px" },
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "0.8125rem",
    fontWeight: 500,
  },
  // Autocomplete chevron + Select chevron in teal so users see "openable"
  "& .MuiAutocomplete-popupIndicator, & .MuiSelect-icon": {
    color: "primary.main",
  },
};

const dropdownPopupSlotProps = {
  paper: {
    elevation: 4,
    sx: {
      borderRadius: "8px",
      border: "1px solid",
      borderColor: "divider",
      mt: 0.5,
    },
  },
  listbox: {
    sx: {
      py: 0,
      "& .MuiAutocomplete-option": {
        fontSize: "0.8125rem",
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
        color: "text.primary",
        "&:last-child": { borderBottom: 0 },
        "&:hover": { backgroundColor: "primary.light", color: "text.primary" },
        "&.Mui-focused": {
          backgroundColor: "primary.light !important",
          color: "text.primary !important",
        },
        // Selected option keeps dark text on a soft teal background
        "&[aria-selected='true']": {
          backgroundColor: "primary.light !important",
          color: "text.primary !important",
          fontWeight: 600,
        },
      },
    },
  },
};

const selectMenuProps = {
  PaperProps: {
    elevation: 4,
    sx: {
      borderRadius: "8px",
      border: "1px solid",
      borderColor: "divider",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        fontSize: "0.8125rem",
        py: 0.75,
        borderBottom: "1px solid",
        borderColor: "divider",
        color: "text.primary",
        "&:last-child": { borderBottom: 0 },
        "&:hover": { backgroundColor: "primary.light", color: "text.primary" },
        "&.Mui-selected": {
          backgroundColor: "primary.light !important",
          color: "text.primary !important",
          fontWeight: 600,
        },
      },
    },
  },
};

const QUERY_KEYS = [
  "page",
  "limit",
  "status",
  "priority",
  "search",
  "imei",
  "heading",
  "companyId",
  "responsibleId",
  "resolvedBy",
  "operator",
  "includeDeleted",
];

const readQueryFromParams = (params) => {
  const obj = {};
  QUERY_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v == null || v === "") return;
    if (k === "page" || k === "limit") obj[k] = Number(v);
    else if (k === "includeDeleted") obj[k] = v === "true" || v === "1";
    else if (k === "status") obj[k] = v.split(",").filter(Boolean);
    else obj[k] = v;
  });
  return obj;
};

// Keys that intentionally live only in redux memory and are NEVER
// serialised to the URL. Matches the Companies page behaviour: the
// search filter persists across in-app navigation (because redux state
// stays in memory) but a hard refresh starts with an empty search.
const URL_EXCLUDED_KEYS = new Set(["search", "imei"]);

const buildSearchParams = (q) => {
  const out = {};
  QUERY_KEYS.forEach((k) => {
    if (URL_EXCLUDED_KEYS.has(k)) return;
    const v = q[k];
    if (v == null) return;
    if (Array.isArray(v)) {
      if (v.length === 0) return;
      out[k] = v.join(",");
      return;
    }
    if (typeof v === "string" && !v) return;
    if (k === "includeDeleted" && !v) return;
    if ((k === "page" && v === 1) || (k === "limit" && v === 25)) return;
    out[k] = String(v);
  });
  return out;
};

export default function TicketsPage({ scope = {}, embedded = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    items,
    pagination,
    query,
    loading,
    statuses,
    headings,
    resolvedByOptions,
    adminUsers,
  } = useSelector((s) => s.tickets);
  const authUser = useSelector((s) => s.auth?.user);
  const userKey = authUser?._id || authUser?.id || authUser?.username || "anon";

  // Favorites state — persisted in localStorage per logged-in admin
  const [favorites, setFavorites] = useState(() =>
    readTicketFavorites(userKey)
  );
  // "all" or "favorites" — shown above filters as a quick toggle
  const [view, setView] = useState(() =>
    readTicketFavorites(userKey).length > 0 ? "favorites" : "all"
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [mastersOpen, setMastersOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // Search/imei live only in redux memory — refresh clears them; in-app
  // navigation away and back keeps them. Same UX as the Companies page.
  const [searchInput, setSearchInput] = useState(query.search || query.imei || "");
  const [operatorInput, setOperatorInput] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  // Skip the auto-fetch + URL writeback until URL → state hydration is done,
  // otherwise the initial unfiltered fetch can race the filtered one (and the
  // writeback strips ?imei=... before hydration can read it).
  const [hydrated, setHydrated] = useState(embedded);
  const debounceRef = useRef(null);
  const sentinelRef = useRef(null);

  // company filter — searchable autocomplete state
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyInput, setCompanyInput] = useState("");
  const [companyLoading, setCompanyLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const companyTimerRef = useRef(null);
  const companyReqRef = useRef(0);

  // hydrate query from URL on mount (non-embedded only). search/imei are
  // excluded from URL serialisation entirely, so they're never present here
  // — the redux state carries them across in-app navigation instead.
  useEffect(() => {
    if (embedded) return;
    const fromUrl = readQueryFromParams(searchParams);
    dispatch(setQuery({ ...fromUrl, page: fromUrl.page || 1 }));
    setOperatorInput(fromUrl.operator || "");
    setHydrated(true);
    // eslint-disable-next-line
  }, []);

  // when scope changes (e.g. company tab), enforce companyId filter
  useEffect(() => {
    if (scope?.companyId) {
      dispatch(setQuery({ companyId: scope.companyId, page: 1 }));
    }
  }, [scope?.companyId, dispatch]);

  // load lookups on mount
  useEffect(() => {
    dispatch(fetchHeadings());
    dispatch(fetchResolvedByOptions());
    dispatch(fetchAdminUsersForTickets());
  }, [dispatch]);

  // Reload favorites if the logged-in admin changes
  useEffect(() => {
    setFavorites(readTicketFavorites(userKey));
  }, [userKey]);

  // refetch list when query / view / favorites change; sync URL too. Wait
  // until URL hydration is done to avoid racing the initial fetch.
  useEffect(() => {
    if (!hydrated) return;
    const params = { ...query };
    if (view === "favorites") {
      params.favoriteIds = favorites.join(",");
    }
    dispatch(fetchTickets(params));
    if (!embedded) {
      const next = buildSearchParams(query);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [query, view, favorites, dispatch, hydrated]);

  const onToggleFavorite = (ticket) => {
    const id = String(ticket._id);
    const next = toggleTicketFavorite(favorites, id);
    setFavorites(next);
    writeTicketFavorites(userKey, next);
  };

  // Mobile infinite scroll — fetch next page and append. Same pattern as
  // CompaniesPage / AuditLogPage. Desktop keeps top-pagination.
  const loadMoreRef = useRef();
  loadMoreRef.current = async () => {
    if (loadingMore || loading) return;
    if (pagination.page >= pagination.pages) return;
    setLoadingMore(true);
    const params = {
      ...query,
      page: pagination.page + 1,
      append: true,
    };
    if (view === "favorites") {
      params.favoriteIds = favorites.join(",");
    }
    try {
      await dispatch(fetchTickets(params));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isMobile) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current?.();
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile, items.length, pagination.page, pagination.pages]);

  const update = (patch) => dispatch(setQuery({ ...patch, page: 1 }));

  // Mirror the Companies search pattern: debounce 1s after the last
  // keystroke, then push to redux. Empty input also flushes the filter.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const next = searchInput.trim();
    if (next === (query.search || "")) return;
    debounceRef.current = setTimeout(() => {
      update({ search: next, imei: "" });
    }, 1000);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [searchInput]);

  const onSearchInput = (v) => setSearchInput(v);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    update({ search: searchInput.trim(), imei: "" });
  };

  const onOperatorInput = (v) => {
    setOperatorInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => update({ operator: v.trim() }), 300);
  };

  // Debounced company-search for the dropdown filter
  useEffect(() => {
    if (companyTimerRef.current) clearTimeout(companyTimerRef.current);
    const q = (companyInput || "").trim();
    if (!q || q.length < 2) {
      setCompanyOptions([]);
      setCompanyLoading(false);
      return;
    }
    setCompanyLoading(true);
    companyTimerRef.current = setTimeout(async () => {
      const reqId = ++companyReqRef.current;
      try {
        const { data } = await api.get("/companies", {
          params: { search: q, limit: 20 },
        });
        if (reqId !== companyReqRef.current) return;
        setCompanyOptions(data?.data?.items || []);
      } catch {
        if (reqId === companyReqRef.current) setCompanyOptions([]);
      } finally {
        if (reqId === companyReqRef.current) setCompanyLoading(false);
      }
    }, 300);
    return () => companyTimerRef.current && clearTimeout(companyTimerRef.current);
  }, [companyInput]);

  // Sync selectedCompany when query.companyId changes externally (URL hydrate, scope)
  useEffect(() => {
    if (!query.companyId) {
      setSelectedCompany(null);
      return;
    }
    if (selectedCompany?._id === query.companyId) return;
    // try to resolve from current options first; otherwise fetch by id
    const fromOpts = companyOptions.find((c) => c._id === query.companyId);
    if (fromOpts) {
      setSelectedCompany(fromOpts);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/companies/${query.companyId}`);
        const c = data?.data?.company || null;
        if (c) setSelectedCompany(c);
      } catch {
        /* noop */
      }
    })();
    // eslint-disable-next-line
  }, [query.companyId]);

  const selectedId = searchParams.get("selected");

  const extraFiltersCount = [
    query.priority,
    query.heading,
    !scope?.companyId && query.companyId,
    query.responsibleId,
    query.resolvedBy,
    query.operator,
  ].filter(Boolean).length;

  const openTicket = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("selected", id);
    setSearchParams(next, { replace: true });
  };
  const closeTicket = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("selected");
    setSearchParams(next, { replace: true });
  };

  const openCompany = (e, companyId) => {
    if (!companyId) return;
    e?.stopPropagation();
    navigate(`/companies/${companyId}/details`);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/tickets/export`, {
        params: query,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tickets-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const headerNode = embedded ? null : (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1}>
      <Breadcrumbs
        separator="›"
        sx={{ fontSize: "0.9375rem", display: { xs: "none", sm: "flex" } }}
      >
        <Typography color="text.secondary" sx={{ fontSize: "0.9375rem" }}>Home</Typography>
        <Typography color="text.primary" sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>Tickets</Typography>
      </Breadcrumbs>
      <Stack
        direction="row"
        sx={{
          // Mobile: span full width, three buttons equally spaced.
          // Desktop: hug the right edge with tight spacing.
          flex: { xs: 1, sm: "0 0 auto" },
          justifyContent: { xs: "space-between", sm: "flex-end" },
          alignItems: "center",
          gap: { xs: 0.5, sm: 1 },
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          onClick={exportCsv}
          sx={{
            flex: { xs: 1, sm: "0 0 auto" },
            fontSize: "0.75rem",
          }}
        >
          Export
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
          onClick={() => setMastersOpen(true)}
          sx={{
            flex: { xs: 1, sm: "0 0 auto" },
            fontSize: "0.75rem",
          }}
        >
          Masters
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setCreateOpen(true)}
          sx={{
            flex: { xs: 1, sm: "0 0 auto" },
            fontSize: "0.75rem",
          }}
        >
          {isMobile ? "Add" : "Add Ticket"}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { md: "100%" },
        minHeight: 0,
      }}
    >
      {headerNode}

      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          flex: { md: 1 },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          {/* MOBILE: row 1 = search + filter button (90/10 split), row 2 =
              status + (optional) New button. DESKTOP: everything inline. */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center" }}
            useFlexGap
          >
            <Box
              component="form"
              onSubmit={onSearchSubmit}
              sx={{
                display: "flex",
                flex: { xs: "1 1 auto", md: "1 1 220px" },
                minWidth: 0,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={
                  isMobile
                    ? "Search…"
                    : "Search company, heading, IMEI, mfg id, operator, contact…"
                }
                value={searchInput}
                onChange={(e) => onSearchInput(e.target.value)}
                sx={{ ...fieldSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() => {
                          setSearchInput("");
                          if (debounceRef.current) clearTimeout(debounceRef.current);
                          update({ search: "", imei: "" });
                        }}
                        sx={{ minWidth: 0, p: 0.25 }}
                      >
                        Clear
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {/* All / Favorites view toggle — shown on every breakpoint */}
            <TextField
              size="small"
              select
              label="View"
              value={view}
              onChange={(e) => setView(e.target.value)}
              sx={{
                ...dropdownFieldSx,
                flex: { xs: "0 0 auto", md: "0 1 130px" },
                minWidth: { xs: 110, md: 110 },
              }}
              SelectProps={{ MenuProps: selectMenuProps }}
            >
              <MenuItem value="all" sx={{ fontSize: "0.8125rem" }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <FilterListIcon sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: "0.8125rem" }}>All</Typography>
                </Stack>
              </MenuItem>
              <MenuItem value="favorites" sx={{ fontSize: "0.8125rem" }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <StarIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                  <Typography sx={{ fontSize: "0.8125rem" }}>
                    {isMobile ? "Fav" : "Favorites"} ({favorites.length})
                  </Typography>
                </Stack>
              </MenuItem>
            </TextField>
            {/* Desktop: status sits inline beside the search box */}
            {!isMobile && (
              <TextField
                size="small"
                select
                label="Status"
                value={Array.isArray(query.status) ? query.status : []}
                onChange={(e) =>
                  update({
                    status: Array.isArray(e.target.value)
                      ? e.target.value
                      : [e.target.value].filter(Boolean),
                  })
                }
                SelectProps={{
                  multiple: true,
                  MenuProps: selectMenuProps,
                  renderValue: (selected) => {
                    const arr = Array.isArray(selected) ? selected : [];
                    if (arr.length === 0) return "All";
                    if (arr.length === statuses.length) return "All";
                    if (arr.length <= 2)
                      return arr.map((s) => STATUS_LABELS[s] || s).join(", ");
                    return `${arr.length} selected`;
                  },
                }}
                sx={{ ...dropdownFieldSx, flex: "0 1 180px", minWidth: 130 }}
              >
                {statuses.map((s) => {
                  const checked = (query.status || []).includes(s);
                  return (
                    <MenuItem key={s} value={s} sx={{ fontSize: "0.8125rem" }}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "3px",
                          border: "1.5px solid",
                          borderColor: checked ? "primary.main" : "divider",
                          backgroundColor: checked
                            ? "primary.main"
                            : "transparent",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      >
                        {checked && (
                          <Box
                            component="span"
                            sx={{ fontSize: "0.6875rem", lineHeight: 1 }}
                          >
                            ✓
                          </Box>
                        )}
                      </Box>
                      {STATUS_LABELS[s] || s}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
            {/* Filter toggle — visible on both web and mobile. Mobile is a
                compact icon button; desktop shows a text button. */}
            {isMobile ? (
              <IconButton
                onClick={() => setMoreOpen((v) => !v)}
                color={moreOpen ? "primary" : "default"}
                sx={{
                  flexShrink: 0,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: moreOpen ? "primary.main" : "divider",
                  backgroundColor: moreOpen ? "primary.light" : "transparent",
                }}
              >
                <Badge
                  badgeContent={extraFiltersCount}
                  color="error"
                  invisible={!extraFiltersCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.5625rem",
                      height: 14,
                      minWidth: 14,
                    },
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </Badge>
              </IconButton>
            ) : (
              <Button
                size="small"
                variant={moreOpen ? "contained" : "outlined"}
                onClick={() => setMoreOpen((v) => !v)}
                startIcon={
                  <Badge
                    badgeContent={extraFiltersCount}
                    color="error"
                    invisible={!extraFiltersCount}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.5625rem",
                        height: 14,
                        minWidth: 14,
                      },
                    }}
                  >
                    <FilterListIcon fontSize="small" />
                  </Badge>
                }
                sx={{ flexShrink: 0 }}
              >
                Filters
              </Button>
            )}
            {/* Embedded mode (e.g. Company > Tickets tab) gets its own +New
                button — page header is hidden in embedded mode, so this is
                the only way to create. Icon-only on mobile, full button on
                desktop. */}
            {embedded &&
              (isMobile ? (
                <IconButton
                  color="primary"
                  onClick={() => setCreateOpen(true)}
                  sx={{
                    flexShrink: 0,
                    backgroundColor: "primary.main",
                    color: "common.white",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "primary.dark" },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setCreateOpen(true)}
                  sx={{ flexShrink: 0 }}
                >
                  New
                </Button>
              ))}
          </Stack>

          <Collapse in={moreOpen} timeout={200}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: "wrap",
                rowGap: 1,
                alignItems: { xs: "flex-start", md: "center" },
                mt: 1,
                pt: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
              useFlexGap
            >
              {/* Mobile-only: status moves into the expanded filter section
                  so the toolbar stays clean (search + filter button only).
                  Sits at 50% width to share a row with the Priority field. */}
              {isMobile && (
                <TextField
                  size="small"
                  select
                  label="Status"
                  value={Array.isArray(query.status) ? query.status : []}
                  onChange={(e) =>
                    update({
                      status: Array.isArray(e.target.value)
                        ? e.target.value
                        : [e.target.value].filter(Boolean),
                    })
                  }
                  SelectProps={{
                    multiple: true,
                    MenuProps: selectMenuProps,
                    renderValue: (selected) => {
                      const arr = Array.isArray(selected) ? selected : [];
                      if (arr.length === 0) return "All";
                      if (arr.length === statuses.length) return "All";
                      if (arr.length <= 2)
                        return arr
                          .map((s) => STATUS_LABELS[s] || s)
                          .join(", ");
                      return `${arr.length} selected`;
                    },
                  }}
                  sx={{
                    ...dropdownFieldSx,
                    flex: "1 1 calc(50% - 4px)",
                    minWidth: 0,
                  }}
                >
                  {statuses.map((s) => {
                    const checked = (query.status || []).includes(s);
                    return (
                      <MenuItem
                        key={s}
                        value={s}
                        sx={{ fontSize: "0.8125rem" }}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "3px",
                            border: "1.5px solid",
                            borderColor: checked ? "primary.main" : "divider",
                            backgroundColor: checked
                              ? "primary.main"
                              : "transparent",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1,
                            flexShrink: 0,
                          }}
                        >
                          {checked && (
                            <Box
                              component="span"
                              sx={{ fontSize: "0.6875rem", lineHeight: 1 }}
                            >
                              ✓
                            </Box>
                          )}
                        </Box>
                        {STATUS_LABELS[s] || s}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}
              <TextField
                size="small"
                select
                label="Priority"
                value={query.priority || ""}
                onChange={(e) => update({ priority: e.target.value })}
                sx={{
                  ...dropdownFieldSx,
                  flex: {
                    xs: "1 1 calc(50% - 4px)",
                    md: "0 1 140px",
                  },
                  minWidth: 0,
                }}
                SelectProps={{ MenuProps: selectMenuProps }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>
                  All
                </MenuItem>
                {PRIORITY_OPTIONS.map((p) => (
                  <MenuItem
                    key={p.value}
                    value={p.value}
                    sx={{ fontSize: "0.8125rem" }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor:
                            PRIORITY_COLOR[p.value]?.fg || "text.disabled",
                        }}
                      />
                      <Typography sx={{ fontSize: "0.8125rem" }}>
                        {p.label}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Heading"
                value={query.heading || ""}
                onChange={(e) => update({ heading: e.target.value })}
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={{
                  ...dropdownFieldSx,
                  flex: { xs: "1 1 100%", md: "1 1 160px" },
                  width: { xs: "100%", md: "auto" },
                  minWidth: 0,
                }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
                {headings.map((h) => (
                  <MenuItem key={h._id || h.name} value={h.name} sx={{ fontSize: "0.8125rem" }}>
                    {h.name}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                size="small"
                options={companyOptions}
                loading={companyLoading}
                filterOptions={(x) => x}
                value={selectedCompany}
                inputValue={companyInput}
                disabled={Boolean(scope?.companyId)}
                onInputChange={(_, v) => setCompanyInput(v)}
                onChange={(_, v) => {
                  setSelectedCompany(v || null);
                  update({ companyId: v?._id || "" });
                }}
                getOptionLabel={(o) =>
                  o
                    ? `${o.name || ""}${
                        o.manufacturing_id != null ? ` · #${o.manufacturing_id}` : ""
                      }`
                    : ""
                }
                isOptionEqualToValue={(a, b) => a && b && a._id === b._id}
                noOptionsText={
                  companyInput.trim().length < 2
                    ? "Type 2+ chars to search"
                    : "No matches"
                }
                renderOption={(props, opt) => (
                  <li {...props} key={opt._id}>
                    <Stack sx={{ width: "100%", minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>
                          {opt.name || "—"}
                        </Typography>
                        {opt.manufacturing_id != null && (
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "monospace", color: "primary.main", fontSize: "0.6875rem" }}
                          >
                            #{opt.manufacturing_id}
                          </Typography>
                        )}
                      </Stack>
                      {(opt.district || opt.block || opt.panchayat) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {[opt.district, opt.block, opt.panchayat].filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                    </Stack>
                  </li>
                )}
                slotProps={dropdownPopupSlotProps}
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 220px" },
                  width: { xs: "100%", md: "auto" },
                  minWidth: 0,
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                    placeholder="Search by name / mfg id"
                    sx={dropdownFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {companyLoading ? (
                            <CircularProgress color="inherit" size={14} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                size="small"
                select
                label="Responsible"
                value={query.responsibleId || ""}
                onChange={(e) => update({ responsibleId: e.target.value })}
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={{
                  ...dropdownFieldSx,
                  flex: {
                    xs: "1 1 calc(40% - 4px)",
                    md: "1 1 160px",
                  },
                  minWidth: 0,
                }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
                {adminUsers.map((u) => (
                  <MenuItem key={u._id} value={u._id} sx={{ fontSize: "0.8125rem" }}>
                    {u.name || u.username || "—"}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Resolved By"
                value={query.resolvedBy || ""}
                onChange={(e) => update({ resolvedBy: e.target.value })}
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={{
                  ...dropdownFieldSx,
                  flex: {
                    xs: "1 1 calc(60% - 4px)",
                    md: "1 1 140px",
                  },
                  minWidth: 0,
                }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>All</MenuItem>
                {resolvedByOptions.map((o) => (
                  <MenuItem key={o._id || o.name} value={o.name} sx={{ fontSize: "0.8125rem" }}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Operator"
                placeholder="Operator name / phone"
                value={operatorInput}
                onChange={(e) => onOperatorInput(e.target.value)}
                sx={{
                  ...fieldSx,
                  flex: { xs: "1 1 100%", md: "1 1 180px" },
                  width: { xs: "100%", md: "auto" },
                  minWidth: 0,
                }}
              />
            </Stack>
          </Collapse>
        </Box>

        {/* Pagination — desktop only, sits ABOVE the table like Audit Log /
            Companies. Mobile uses infinite scroll. */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            p: 1,
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {items.length} of {pagination.total} · Page{" "}
            {pagination.page} of {Math.max(pagination.pages, 1)}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              size="small"
              page={pagination.page}
              count={Math.max(pagination.pages, 1)}
              onChange={(_, p) => dispatch(setQuery({ page: p }))}
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        </Stack>

        {isMobile ? (
          <Box sx={{ p: 1, backgroundColor: "background.default", flex: 1, overflow: "auto", minHeight: 0 }}>
            {loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <Typography variant="caption" display="block">
                  No tickets found.
                </Typography>
                <MuiLink
                  component="button"
                  onClick={() => setCreateOpen(true)}
                  sx={{ fontSize: "0.75rem", mt: 0.5 }}
                >
                  Create the first one
                </MuiLink>
              </Box>
            )}
            <Stack spacing={0.75}>
              {items.map((t) => (
                <Paper
                  key={t._id}
                  variant="outlined"
                  onClick={() => openTicket(t._id)}
                  sx={{
                    p: 1.25,
                    cursor: "pointer",
                    overflow: "hidden",
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    opacity: t.isdeleted ? 0.55 : 1,
                    // Same light surface as Companies / Search mobile cards.
                    backgroundColor: "#f4f8fc",
                    borderColor: "#dbe6f0",
                    transition: "background-color .15s, box-shadow .15s, border-color .15s",
                    "&:hover": {
                      backgroundColor: "#e8f0fa",
                      borderColor: "primary.main",
                      boxShadow: (th) => th.customShadows?.md,
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                    mb={0.5}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                          wordBreak: "break-word",
                          mb: 0.25,
                          lineHeight: 1.3,
                        }}
                      >
                        {t.heading || "—"}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <TypeChip type={t.deviceType} />
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {t.imei}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <FavoriteStar
                          active={isTicketFavorite(favorites, t._id)}
                          onToggle={() => onToggleFavorite(t)}
                          size={16}
                        />
                        <StatusChip status={t.status} />
                      </Stack>
                      <PriorityChip priority={t.priority} />
                      <ChevronRightIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                    </Stack>
                  </Stack>

                  {(t.company?.name || t.company?.manufacturing_id != null) && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      onClick={(e) => openCompany(e, t.company?.id)}
                      sx={{
                        mb: 0.25,
                        ...(t.company?.id
                          ? {
                              cursor: "pointer",
                              width: "fit-content",
                              borderRadius: "4px",
                              px: 0.25,
                              mx: -0.25,
                              transition: "background-color .15s",
                              "&:active": {
                                backgroundColor: "primary.light",
                              },
                            }
                          : {}),
                      }}
                    >
                      {t.company?.manufacturing_id != null && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            color: "primary.main",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                          }}
                        >
                          #{t.company.manufacturing_id}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          color: "primary.main",
                          textDecoration: t.company?.id ? "underline" : "none",
                          textDecorationColor: "rgba(13,148,136,0.3)",
                        }}
                      >
                        {t.company?.name || "—"}
                      </Typography>
                    </Stack>
                  )}

                  {/* Same compact 3-row contact block (Operator / Electrician
                      / Secretary) as the desktop table. */}
                  <Box sx={{ mt: 0.5 }}>
                    <TableContactCell company={t.company} />
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ rowGap: 0.25, mt: 0.25 }}
                  >
                    {(t.responsiblePerson?.name || t.responsiblePerson?.username) && (
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <PersonIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          {t.responsiblePerson.name || t.responsiblePerson.username}
                        </Typography>
                      </Stack>
                    )}
                    {t.updates?.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        💬 {t.updates.length}
                      </Typography>
                    )}
                  </Stack>

                  {t.createdAt && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        mt: 0.5,
                        pt: 0.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: "text.secondary",
                          minWidth: 40,
                        }}
                      >
                        Date:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                      >
                        {dayjs(t.createdAt).format("DD-MM-YYYY hh:mm A")}
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              ))}
            </Stack>

            {/* Infinite-scroll sentinel + status text (mobile only) */}
            {items.length > 0 && (
              <Box
                ref={sentinelRef}
                sx={{ py: 2, textAlign: "center", color: "text.secondary" }}
              >
                {loadingMore ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress size={16} />
                    <Typography variant="caption">Loading more…</Typography>
                  </Stack>
                ) : pagination.page < pagination.pages ? (
                  <Typography variant="caption">Scroll for more</Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    All {pagination.total} tickets loaded
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                // Fixed layout so every column has identical width on every
                // row — required for the Contact column's inner grid to line
                // up vertically across rows (same fix as Companies / Search).
                tableLayout: "fixed",
                // Close the bottom edge of the table when there are only a
                // few rows — otherwise MUI strips the last row's border and
                // the table looks "open" (same fix as the companies list).
                "& tbody tr:last-child .MuiTableCell-root": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell sx={{ width: 85, whiteSpace: "nowrap" }}>Date</TableCell>
                  <TableCell sx={{ width: 220 }}>Company</TableCell>
                  <TableCell sx={{ width: 280 }}>Contact</TableCell>
                  <TableCell>Heading</TableCell>
                  <TableCell sx={{ width: 100 }}>Status</TableCell>
                  <TableCell sx={{ width: 140 }}>Responsible</TableCell>
                  <TableCell sx={{ width: 28, px: 0.25 }} align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No tickets found.{" "}
                      <MuiLink component="button" onClick={() => setCreateOpen(true)}>
                        Create the first one
                      </MuiLink>
                    </TableCell>
                  </TableRow>
                )}
                {items.map((t, i) => (
                  <TableRow
                    key={t._id}
                    hover
                    sx={{ cursor: "pointer", opacity: t.isdeleted ? 0.55 : 1 }}
                    onClick={() => openTicket(t._id)}
                  >
                    <TableCell sx={{ color: "text.secondary" }}>
                      {(pagination.page - 1) * pagination.limit + i + 1}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                      {t.createdAt ? (
                        <Stack spacing={0}>
                          <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                            {dayjs(t.createdAt).format("DD-MM-YYYY")}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.625rem",
                              lineHeight: 1.2,
                              color: "text.disabled",
                              fontFamily: "monospace",
                            }}
                          >
                            {dayjs(t.createdAt).format("hh:mm A")}
                          </Typography>
                        </Stack>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        onClick={(e) => openCompany(e, t.company?.id)}
                        sx={
                          t.company?.id
                            ? {
                                cursor: "pointer",
                                width: "fit-content",
                                borderRadius: "4px",
                                px: 0.25,
                                mx: -0.25,
                                transition: "background-color .15s",
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                  "& .ticket-company-name": {
                                    color: "primary.main",
                                    textDecoration: "underline",
                                  },
                                },
                              }
                            : undefined
                        }
                      >
                        {t.company?.manufacturing_id != null && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              color: "primary.main",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                            }}
                          >
                            #{t.company.manufacturing_id}
                          </Typography>
                        )}
                        <Typography
                          className="ticket-company-name"
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            color: "text.primary",
                            // Wrap rather than truncate so long company names
                            // are fully visible (no "...").
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {t.company?.name || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TableContactCell company={t.company} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Stack spacing={0.25}>
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
                        >
                          {t.heading || "—"}
                        </Typography>
                        {/* Device type chip + IMEI + copy button under heading. */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ rowGap: 0.25 }}
                        >
                          <TypeChip type={t.deviceType} />
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.6875rem",
                              color: "text.secondary",
                              wordBreak: "break-all",
                            }}
                          >
                            {t.imei}
                          </Typography>
                          {t.imei && (
                            <Tooltip title="Copy IMEI">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard
                                    .writeText(t.imei)
                                    .then(() => toast.success("IMEI copied"))
                                    .catch(() => toast.error("Copy failed"));
                                }}
                                sx={{
                                  p: 0.25,
                                  color: "text.disabled",
                                  "&:hover": { color: "primary.main" },
                                }}
                              >
                                <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                        {t.updates?.length > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.6875rem" }}
                          >
                            {t.updates.length} update
                            {t.updates.length === 1 ? "" : "s"}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell><StatusChip status={t.status} /></TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {t.responsiblePerson?.name || t.responsiblePerson?.username || "—"}
                    </TableCell>
                    <TableCell sx={{ px: 0.25 }} align="center">
                      <FavoriteStar
                        active={isTicketFavorite(favorites, t._id)}
                        onToggle={() => onToggleFavorite(t)}
                        size={18}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <TicketCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        prefillImei={scope?.imei || query.imei || ""}
        query={query}
      />

      <MasterListsDialog open={mastersOpen} onClose={() => setMastersOpen(false)} />

      <TicketDetailDrawer
        ticketId={selectedId}
        onClose={closeTicket}
      />
    </Box>
  );
}
